/**
 * Real, in-browser backend for the ADS Desk UI — replaces the Tauri/Python/
 * Docker engine with this project's WASM/ONNX pipeline. There is no
 * filesystem and no subprocess: "subjectDir" is a synthetic id pointing at an
 * in-memory FileList the user picked via `<input webkitdirectory>`, and a
 * "job" runs real workers in this tab instead of spawning a process.
 *
 * Scope: for DWI, `skull_strip`/`gen_mask`, `registration` (real ANTs
 * Affine+SyN, via `lib/dwi-pipeline.ts`), `inference` (real DAGMNet DWI
 * ONNX), and `report` now actually run. Report includes real atlas
 * overlap/volumes AND real region classification (28 of the real pipeline's
 * ~34 AA_models RandomForest region classifiers, converted to ONNX and run
 * client-side — see `lib/report/interpretation.ts`; the other 6 need a
 * BPM/ventricle atlas this app does not bundle, reported as unavailable
 * rather than faked). `prepdata` (shared by DWI and PWI) now really runs
 * too: real 4D DWI/b0 order detection + real ADC calculation when no
 * separate ADC file is given (`lib/dwi-prep.ts`, ported from and numerically
 * validated against the real `VolumeOrderDetector`/`ADCCalculator` — a
 * no-op, bit-identical to the prior passthrough, when an ADC file already
 * exists, which is the bundled demo subject's case).
 *
 * For PWI, `skull_strip`/`gen_mask`, `gen_ttp` (real slice-time correction +
 * Gd concentration + peak detection), `registration` (real two-step
 * coregistration, via `lib/pwi-pipeline.ts`), `ttpadc_coreg` (folded into
 * `registration` — its own step 1), `inference` (real DAGMNet PWI ONNX,
 * HPsymclassic channel deliberately zero-filled, see
 * `pwi-pipeline.ts`'s `runPwiSegmentation` header) and `report` (same shared
 * `generateReport()` DWI uses) now actually run too. PWI's own registration
 * is not self-contained — it reuses DWI's native brain mask and native->MNI
 * affine transform (`registration_pwi_align.py`'s real design: both
 * modalities land in the same space so core and hypoperfusion can later be
 * compared), so a PWI run internally runs DWI's skull-strip + registration
 * first if the subject's DWI/ADC files are present and that hasn't already
 * happened in this job — see `ensureDwiPrereqForPwi`. A real DWI core vs.
 * PWI hypoperfusion "mismatch" calculation does not exist anywhere in this
 * project yet (not in the validated reference script either) — out of
 * scope, not silently invented.
 *
 * Every stage this file cannot really run is reported `skipped` with an
 * honest reason string rather than silently pretending to run.
 */
import { mockBus } from "../ipc/mocks";
import { appPath, fetchAsset } from "../lib/assets";
import { runDwiRegistration, runDwiSegmentation, warpGroundTruthToMni } from "../lib/dwi-pipeline";
import { runPwiTtp, runPwiRegistration, runPwiSegmentation, restorePwiHpToNativeSpace } from "../lib/pwi-pipeline";
import { generateReport, buildReportFileSet } from "../lib/report";
import { niftiGz, maskToVol } from "../lib/export";
import type { NiftiVolume } from "../lib/nifti";
import {
  DWI_STAGES,
  DWI_STAGE_TITLES,
  PWI_STAGES,
  PWI_STAGE_TITLES,
  EVT_JOB_LOG,
  EVT_JOB_PROGRESS,
  EVT_JOB_STAGE,
  EVT_JOB_STATE,
  type CaseDraft,
  type CaseManifest,
  type DetectedFile,
  type DwiStage,
  type JobHandle,
  type JobSpec,
  type JobStatus,
  type OutputNode,
  type PipelineKind,
  type PwiStage,
  type Readiness,
  type RecentCase,
  type StageId,
  type StageSelection,
  type StageStatus,
  type SubjectScan,
  type SystemStatus,
  type EngineStatus,
} from "../ipc/contract";
import { loadNiftiFile, loadNiftiFile4D, extractFrame3D } from "../lib/nifti";
import { runAntsApplyTransform } from "../lib/ants-registration-client";
import { postprocessDwi, postprocessHp, segMetrics } from "../lib/segPostproc";
import { buildInterpretationPdfs, qfvCsvToFeatureMap } from "../lib/report/shapPdf";
import { renderDwiStrokeFigure, renderHpFigure } from "../lib/report/figures";
import { runWorker, ab } from "../lib/worker-client";
import { prepareRawDwiData, type PreparedDwiRaw } from "../lib/dwi-prep";
import { percentileClipRescaleInMask, zscoreInMask } from "../lib/volume";
import { saveCaseRecord, loadCaseRecord, deleteCaseRecord, listCaseRecords } from "../lib/project-store";

// ---------------------------------------------------------- file store ---

/** subjectDir -> the files the user picked for it. Never touches disk. */
const fileStore = new Map<string, File[]>();
/** Output blobs a job produced, keyed by the synthetic relPath the UI is told about. */
const outputStore = new Map<string, { blob: Blob; kind: OutputNode["kind"] }>();

// Writers for the native OpenADS output tree (`<subject>/<MOD>/<stage>/<file>`,
// stored here under `<jobId>/<MOD>/<stage>/<file>`).
async function putVol(path: string, vol: NiftiVolume): Promise<void> {
  outputStore.set(path, { blob: new Blob([(await niftiGz(vol)) as BlobPart], { type: "application/gzip" }), kind: "nifti" });
}
async function putMask(path: string, mask: ArrayLike<number>, ref: { dims: number[] | readonly number[]; pixdim: number[] | readonly number[]; affine: Float32Array }): Promise<void> {
  await putVol(path, maskToVol(Uint8Array.from(mask), ref.dims as never, ref.pixdim as never, ref.affine));
}
function putText(path: string, text: string, type = "text/plain"): void {
  outputStore.set(path, { blob: new Blob([text], { type }), kind: type === "application/json" ? "json" : "other" });
}
function putFile(path: string, file: Blob, kind: OutputNode["kind"]): void {
  outputStore.set(path, { blob: file, kind });
}
function copyOutput(from: string, to: string): void {
  const entry = outputStore.get(from);
  if (entry) outputStore.set(to, entry);
}
function maskedVol(vol: NiftiVolume, mask: Uint8Array): NiftiVolume {
  const voxels = new Float32Array(vol.voxels.length);
  for (let i = 0; i < voxels.length; i++) voxels[i] = mask[i] ? vol.voxels[i] : 0;
  return { ...vol, voxels };
}
/** Minimal YAML for the registration manifest (nested string maps only). */
function toYaml(value: unknown, indent = ""): string {
  if (value === null || typeof value !== "object") return `${JSON.stringify(value)}\n`;
  if (Array.isArray(value)) return value.map((v) => `\n${indent}- ${toYaml(v, indent + "  ").trimStart()}`).join("").replace(/^\n/, "\n");
  return Object.entries(value as Record<string, unknown>)
    .map(([k, v]) => (v !== null && typeof v === "object" ? `${indent}${k}:\n${toYaml(v, indent + "  ")}` : `${indent}${k}: ${toYaml(v)}`))
    .join("");
}
/** Ground truth warped to MNI-affine during registration, kept for metrics.json. */
const gtAffByJob = new Map<string, NiftiVolume>();
/** Reference HP mask (native PWI grid) and its MNI-affine warp, for PWI metrics.json. */
const hpGtByJob = new Map<string, { native: Float32Array; mni: Float32Array }>();

/**
 * For a tool that is not a pipeline stage — a one-off like SAM/MedSAM, run
 * directly against whatever image was open rather than as a step of the
 * sub-{id}/{DWI,PWI}/... run. `relPath` must start with `tools/<tool-id>/` so
 * Output's listing never mixes a one-off run in with a real pipeline stage's
 * files (see `tools/segment/InteractiveSegPanel.tsx`, the only caller today).
 */
export function registerToolOutput(relPath: string, blob: Blob, kind: OutputNode["kind"]): void {
  if (!relPath.startsWith("tools/")) {
    throw new Error(`registerToolOutput: relPath must start with "tools/", got "${relPath}"`);
  }
  outputStore.set(relPath, { blob, kind });
}

function relPathOf(file: File): string {
  // webkitRelativePath looks like "sub-02e8eb42/DWI.nii.gz"; strip the
  // subject-folder segment so relPath is relative to the subject dir, matching
  // what a real filesystem scan would report.
  const p = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
  const parts = p.split("/");
  return parts.length > 1 ? parts.slice(1).join("/") : parts[0];
}

/** Browser replacement for the Tauri native folder picker. */
export function pickDirectoryWeb(): Promise<{ path: string | null }> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    (input as HTMLInputElement & { webkitdirectory: boolean }).webkitdirectory = true;
    input.multiple = true;
    input.style.display = "none";
    input.addEventListener(
      "change",
      () => {
        const files = Array.from(input.files ?? []);
        document.body.removeChild(input);
        if (files.length === 0) {
          resolve({ path: null });
          return;
        }
        const first = (files[0] as File & { webkitRelativePath?: string }).webkitRelativePath || "";
        const subjectId = first.split("/")[0] || `subject-${Date.now()}`;
        const subjectDir = `browser://${subjectId}`;
        fileStore.set(subjectDir, files);
        resolve({ path: subjectDir });
      },
      { once: true },
    );
    // Cancel fires no event browsers agree on; the Promise simply never
    // resolves in that case, matching Tauri's own `pick_directory` contract
    // ("path: null" is for a dialog that opened and was dismissed, which a
    // native `<input>` cancel does not expose either way).
    document.body.appendChild(input);
    input.click();
  });
}

export function isBrowserSubjectDir(dir: string): boolean {
  return dir.startsWith("browser://");
}

/** The real validation subject (Dice=0.8439 DWI registration+segmentation,
 *  NCC=0.9984 ANTs registration — see `lib/dwi-pipeline.ts`'s header),
 *  already served statically from `public/demo/raw-dwi/`. Fetched instead of
 *  picked so this path works both for a human clicking a button and for
 *  browser automation, neither of which can drive the native OS directory
 *  picker `pickDirectoryWeb` above depends on. */
const EXAMPLE_SUBJECT_ID = "sub-02e8eb42";
/** The two OpenADS example subjects (`assets/examples/{dwi,pwi}/sub-02e8eb42`). */
const EXAMPLE_SUBJECTS: Record<"dwi" | "pwi", { folder: string; files: string[] }> = {
  // Images only: the reference labels (stroke, HP_manual) are left out, so the
  // example runs the way a real case does (no ground truth, no Dice).
  dwi: { folder: "raw-dwi", files: ["02e8eb42_DWI.nii.gz", "02e8eb42_b0.nii.gz"] },
  pwi: {
    folder: "raw-pwi",
    files: [
      "02e8eb42_ADC.nii.gz",
      "02e8eb42_b0.nii.gz",
      "02e8eb42_DWI.nii.gz",
      "02e8eb42_PWI.json",
      "02e8eb42_PWI.nii.gz",
      "sub-02e8eb42_HP_symClassic.nii.gz",
    ],
  },
};

export async function loadExampleSubjectWeb(kind: "dwi" | "pwi" = "dwi"): Promise<{ path: string }> {
  const example = EXAMPLE_SUBJECTS[kind];
  const files: File[] = [];
  for (const name of example.files) {
    const res = await fetchAsset(`/demo/${example.folder}/${name}`);
    if (!res.ok) throw new Error(`Example subject: could not fetch ${name} (${res.status})`);
    const blob = await res.blob();
    const file = new File([blob], name, { type: name.endsWith(".json") ? "application/json" : "application/gzip" });
    // webkitRelativePath is non-writable on a constructed File; redefine it
    // (configurable on every browser this app targets) so relPathOf() and
    // pickDirectoryWeb's own subjectId-from-first-file logic both see it as
    // if it came from a real `sub-02e8eb42/` folder pick.
    Object.defineProperty(file, "webkitRelativePath", {
      value: `${EXAMPLE_SUBJECT_ID}/${name}`,
      configurable: true,
    });
    files.push(file);
  }
  const subjectDir = `browser://${EXAMPLE_SUBJECT_ID}`;
  fileStore.set(subjectDir, files);
  return { path: subjectDir };
}

function filesFor(subjectDir: string): File[] {
  return fileStore.get(subjectDir) ?? [];
}

// --------------------------------------------------------------- scan ---

const MODALITY_PATTERNS: [RegExp, DetectedFile["modality"]][] = [
  [/stroke/i, "Stroke"],
  [/mask/i, "Mask"],
  [/hp[_-]?manual|hp[_-]?sym/i, "HP"],
  [/adc/i, "ADC"],
  [/b0/i, "b0"],
  [/ttp/i, "TTP"],
  [/pwi/i, "PWI"],
  [/dwi/i, "DWI"],
];

function classifyModality(fileName: string): DetectedFile["modality"] {
  for (const [re, m] of MODALITY_PATTERNS) if (re.test(fileName)) return m;
  return "Unknown";
}

function fileType(fileName: string): DetectedFile["fileType"] {
  if (/\.nii(\.gz)?$/i.test(fileName)) return "NIfTI";
  if (/\.json$/i.test(fileName)) return "JSON";
  return "Other";
}

export async function scanSubjectWeb(subjectDir: string): Promise<SubjectScan> {
  const files = filesFor(subjectDir);
  const subjectId = subjectDir.replace(/^browser:\/\//, "");
  const detected: DetectedFile[] = [];
  let dims: number[] | null = null;
  let voxel: number[] | null = null;

  for (const f of files) {
    const rel = relPathOf(f);
    const type = fileType(f.name);
    const modality = classifyModality(f.name);
    let detail = "OK";
    let status: DetectedFile["status"] = "ok";
    if (type === "NIfTI") {
      try {
        const vol = await loadNiftiFile(f);
        dims = vol.dims as unknown as number[];
        voxel = vol.pixdim as unknown as number[];
      } catch (err) {
        status = "warning";
        detail = `Could not parse header: ${(err as Error).message}`;
      }
    }
    detected.push({
      path: `${subjectDir}/${rel}`,
      fileName: f.name,
      relPath: rel,
      fileType: type,
      modality,
      sizeBytes: f.size,
      dimensions: dims,
      voxelSizeMm: voxel,
      datatype: type === "NIfTI" ? "float32" : null,
      status,
      detail,
    });
  }

  const has = (m: DetectedFile["modality"]) => detected.some((d) => d.modality === m);
  const dwiReady: Readiness = has("DWI")
    ? { ready: true, missing: [], notes: [] }
    : { ready: false, missing: ["DWI"], notes: ["No DWI volume found in this folder."] };
  const pwiReady: Readiness = has("PWI")
    ? { ready: true, missing: [], notes: [] }
    : { ready: false, missing: ["PWI"], notes: ["No PWI series found in this folder."] };

  return {
    subjectDir,
    subjectId,
    baseId: subjectId.replace(/^sub-/, ""),
    files: detected,
    validCount: detected.filter((d) => d.status === "ok").length,
    warningCount: detected.filter((d) => d.status === "warning").length,
    errorCount: detected.filter((d) => d.status === "error").length,
    isDatasetRoot: false,
    readiness: {
      dwi: dwiReady,
      pwi: pwiReady,
      combined: dwiReady.ready && pwiReady.ready ? { ready: true, missing: [], notes: [] } : { ready: false, missing: [...dwiReady.missing, ...pwiReady.missing], notes: [] },
    },
  };
}

// --------------------------------------------------------------- case ---

let caseSeq = 0;
const cases = new Map<string, CaseManifest>();

export function createCaseWeb(draft: CaseDraft): CaseManifest {
  const caseId = `case-${++caseSeq}`;
  const now = new Date().toISOString();
  const manifest: CaseManifest = {
    schemaVersion: 1,
    caseId,
    subjectId: draft.subjectId,
    studyName: draft.studyName,
    pipelineKind: draft.pipelineKind,
    createdIso: now,
    modifiedIso: now,
    inputRoot: draft.subjectDir,
    subjectRelPath: "",
    pwiSubjectRelPath: draft.pwiSubjectDir ?? null,
    outputRoot: "browser://output",
    gpuIndex: "cpu",
    dockerImage: null,
    stages: draft.stages,
    cleanupEnabled: draft.cleanupEnabled,
    notes: "",
    images: [],
    masks: [],
    transforms: [],
    atlases: [],
    measurements: [],
    reports: [],
  };
  cases.set(caseId, manifest);
  return manifest;
}

export function getCaseWeb(caseId: string): CaseManifest | null {
  return cases.get(caseId) ?? null;
}

/** Durable case-metadata save (IndexedDB) — see `lib/project-store.ts`. Does
 *  NOT persist this case's run outputs (`outputStore` stays in-memory only);
 *  reopening a saved case restores its settings, not its completed stages. */
export async function saveProjectWeb(caseId: string): Promise<CaseManifest> {
  const manifest = cases.get(caseId);
  if (!manifest) throw new Error(`No open case "${caseId}" to save.`);
  const saved = await saveCaseRecord(manifest);
  cases.set(caseId, saved);
  return saved;
}

export async function listRecentCasesWeb(): Promise<RecentCase[]> {
  return listCaseRecords();
}

/** `caseJsonPath` is this build's `indexeddb://openads-projects/<caseId>` URI
 *  (see `project-store.ts#listCaseRecords`) — extract the id and reload. */
export async function openCaseWeb(caseJsonPath: string): Promise<CaseManifest> {
  const caseId = caseJsonPath.split("/").pop();
  const manifest = caseId ? await loadCaseRecord(caseId) : null;
  if (!manifest) throw new Error(`Saved project not found: ${caseJsonPath}`);
  cases.set(manifest.caseId, manifest);
  return manifest;
}

export async function deleteProjectWeb(caseId: string): Promise<void> {
  await deleteCaseRecord(caseId);
}

// ---------------------------------------------------------------- job ---

const jobs = new Map<string, JobStatus>();
let jobSeq = 0;

function nowIso() {
  return new Date().toISOString();
}

function stageTitle(id: StageId): string {
  return (DWI_STAGE_TITLES as Record<string, string>)[id] ?? (PWI_STAGE_TITLES as Record<string, string>)[id] ?? id;
}

function emitLog(jobId: string, line: string, level: "info" | "warn" | "error" = "info") {
  mockBus.emit(EVT_JOB_LOG, { jobId, entries: [{ seq: Date.now(), ts: nowIso(), line, level }] });
}

function emitStage(jobId: string, modality: "DWI" | "PWI", stage: StageId, state: StageStatus["state"]) {
  mockBus.emit(EVT_JOB_STAGE, { jobId, stage, modality, state });
  const job = jobs.get(jobId);
  if (!job) return;
  const s = job.stages.find((x) => x.id === stage && x.modality === modality);
  if (s) {
    s.state = state;
    if (state === "running") s.startedIso = nowIso();
    if (state === "completed" || state === "failed" || state === "skipped") s.finishedIso = nowIso();
  }
}

function emitProgress(jobId: string) {
  const job = jobs.get(jobId);
  if (!job) return;
  const total = job.stages.length || 1;
  const done = job.stages.filter((s) => s.state === "completed" || s.state === "skipped" || s.state === "failed").length;
  job.progressPct = Math.round((done / total) * 100);
  mockBus.emit(EVT_JOB_PROGRESS, { jobId, progressPct: job.progressPct, currentStage: job.currentStage });
}

function emitState(jobId: string, state: JobStatus["state"], errorMessage: string | null = null) {
  const job = jobs.get(jobId);
  if (job) {
    job.state = state;
    job.errorMessage = errorMessage;
    job.finishedIso = state === "completed" || state === "failed" || state === "cancelled" ? nowIso() : job.finishedIso;
  }
  mockBus.emit(EVT_JOB_STATE, { jobId, state, exitCode: state === "completed" ? 0 : state === "failed" ? 1 : null, errorMessage });
}

/** Stages with no real implementation wired (both DWI's and PWI's
 *  registration/inference/report/gen_ttp are handled separately below, per
 *  modality, not through this map — this only covers what's genuinely not
 *  wired for either modality). */
const NOT_YET_WIRED: Partial<Record<StageId, string>> = {
  gen_mask: "Folded into Skull Strip for this pass — SynthStrip produces the brain mask and the stripped volume together.",
};

/**
 * Real `RawDataProcessor.run()` (preprocessing_dwi_prepare.py), for real:
 * if an ADC file already exists (the bundled demo subject's case), this is a
 * no-op passthrough exactly like before. Only when no separate ADC is given
 * does it split a 4D DWI/b0 pair (with real order detection) and compute ADC
 * — see `lib/dwi-prep.ts`, numerically validated against the real Python
 * `ADCCalculator`/`VolumeOrderDetector` (max float32-epsilon diff).
 */
async function runDwiPrepStage(
  jobId: string,
  modality: "DWI" | "PWI",
  subjectId: string,
  files: File[],
): Promise<PreparedDwiRaw | null> {
  emitStage(jobId, modality, "prepdata", "running");
  // PWI's own raw prep reuses the same DWI/ADC/b0 inputs as DWI's.
  const dwiFile = files.find((f) => classifyModality(f.name) === "DWI");
  if (!dwiFile) {
    emitLog(jobId, `[${modality}] Prep: no DWI file found — skipping`, "warn");
    emitStage(jobId, modality, "prepdata", "skipped");
    return null;
  }
  const adcFile = files.find((f) => classifyModality(f.name) === "ADC");
  const b0File = files.find((f) => classifyModality(f.name) === "b0");
  try {
    const existingAdc = adcFile ? await loadNiftiFile(adcFile) : null;
    const existingB0 = b0File ? await loadNiftiFile(b0File) : null;

    let dwiVol: NiftiVolume;
    let pairB0: NiftiVolume | null = null;
    const vol4d = await loadNiftiFile4D(dwiFile).catch(() => null);
    if (vol4d && vol4d.dims[3] >= 2) {
      emitLog(jobId, `[${modality}] Prep: raw DWI is 4D (${vol4d.dims.join("x")}) — extracting DWI/b0 volumes`);
      dwiVol = extractFrame3D(vol4d, 0);
      pairB0 = extractFrame3D(vol4d, 1);
    } else {
      dwiVol = await loadNiftiFile(dwiFile);
    }

    const prepared = prepareRawDwiData(dwiVol, pairB0, existingAdc, existingB0);
    if (prepared.adcWasComputed) {
      emitLog(
        jobId,
        `[${modality}] Prep: ✓ computed ADC from DWI+b0${prepared.orderWasSwapped ? " (DWI/b0 order was swapped, corrected)" : ""} — real ADCCalculator formula (-ln((DWI+eps)/(b0+eps))/bvalue)`,
      );
    } else {
      emitLog(jobId, `[${modality}] Prep: using existing ADC file as-is (matches real RawDataProcessor's no-recompute path)`);
    }

    const root = `${jobId}/${modality}/preprocess`;
    await Promise.all([
      outputStore.set(`${root}/${subjectId}_DWI.nii.gz`, { blob: new Blob([(await niftiGz(prepared.dwi)) as BlobPart], { type: "application/gzip" }), kind: "nifti" }),
      outputStore.set(`${root}/${subjectId}_ADC.nii.gz`, { blob: new Blob([(await niftiGz(prepared.adc)) as BlobPart], { type: "application/gzip" }), kind: "nifti" }),
      ...(prepared.b0
        ? [outputStore.set(`${root}/${subjectId}_B0.nii.gz`, { blob: new Blob([(await niftiGz(prepared.b0)) as BlobPart], { type: "application/gzip" }), kind: "nifti" })]
        : []),
    ]);

    const strokeFile = files.find((f) => classifyModality(f.name) === "Stroke" && fileType(f.name) === "NIfTI");
    if (strokeFile) putFile(`${root}/${subjectId}_stroke.nii.gz`, strokeFile, "nifti");

    emitStage(jobId, modality, "prepdata", "completed");
    return prepared;
  } catch (err) {
    emitLog(jobId, `[${modality}] Prep failed: ${(err as Error).message}`, "error");
    emitStage(jobId, modality, "prepdata", "failed");
    return null;
  }
}

/** Native preprocess's skull-stripped copies (`_DWI_brain`, `_ADC_brain`, `_B0_brain`). */
async function writeDwiBrainCopies(
  jobId: string,
  modality: "DWI" | "PWI",
  subjectId: string,
  raw: PreparedDwiRaw | null,
  mask: Uint8Array,
  withB0: boolean,
): Promise<void> {
  if (!raw) return;
  const root = `${jobId}/${modality}/preprocess`;
  await putVol(`${root}/${subjectId}_DWI_brain.nii.gz`, maskedVol(raw.dwi, mask));
  await putVol(`${root}/${subjectId}_ADC_brain.nii.gz`, maskedVol(raw.adc, mask));
  if (withB0 && raw.b0) await putVol(`${root}/${subjectId}_B0_brain.nii.gz`, maskedVol(raw.b0, mask));
}

async function runSkullStrip(
  jobId: string,
  modality: "DWI" | "PWI",
  file: File,
  subjectId: string,
  preloadedVol?: NiftiVolume,
): Promise<{ vol: NiftiVolume; mask: Uint8Array } | void> {
  emitStage(jobId, modality, "skull_strip", "running");
  emitLog(jobId, `[${modality}] Performing skull stripping (SynthStrip, ONNX Runtime Web)`);
  try {
    const vol = preloadedVol ?? (await loadNiftiFile(file));
    const worker = new Worker(appPath("workers/synthstrip.worker.js"), { type: "module" });
    const result = await runWorker<
      { voxels: ArrayBuffer; dims: number[]; pixdim: number[]; affine: ArrayBuffer },
      { mask: ArrayBuffer; dims: number[]; brainVoxels: number; totalMs: number }
    >(
      worker,
      {
        voxels: ab(vol.voxels),
        dims: vol.dims as unknown as number[],
        pixdim: vol.pixdim as unknown as number[],
        affine: ab(vol.affine),
      },
      (stage) => emitLog(jobId, `[${modality}]   ${stage}`),
    );
    worker.terminate();
    emitLog(jobId, `[${modality}] ✓ Skull stripping completed — ${result.brainVoxels.toLocaleString()} brain voxels (${result.totalMs} ms)`);

    const mask = new Uint8Array(result.mask);
    const maskVol = maskToVol(mask, vol.dims, vol.pixdim, vol.affine);
    outputStore.set(`${jobId}/${modality}/preprocess/${subjectId}_${modality}brain-mask.nii.gz`, {
      blob: new Blob([(await niftiGz(maskVol)) as BlobPart], { type: "application/gzip" }),
      kind: "nifti",
    });
    emitStage(jobId, modality, "skull_strip", "completed");
    emitStage(jobId, modality, "gen_mask", "completed");
    return { vol, mask };
  } catch (err) {
    emitLog(jobId, `[${modality}] ❌ Skull stripping failed: ${(err as Error).message}`, "error");
    emitStage(jobId, modality, "skull_strip", "failed");
    throw err;
  }
}

/** Real ANTs Affine+SyN registration (DWI only this pass) — see `lib/dwi-pipeline.ts`. */
/** Per job: whether to skip SyN. Set from the spec's `registration.run_syn` override (default: skip). */
const skipSynByJob = new Map<string, boolean>();

async function runDwiRegistrationStage(
  jobId: string,
  subjectId: string,
  prep: { vol: NiftiVolume; mask: Uint8Array },
  adcVol: NiftiVolume,
  rawB0?: NiftiVolume | null,
  files: File[] = [],
  skipSyn = skipSynByJob.get(jobId) ?? true,
) {
  emitStage(jobId, "DWI", "registration", "running");
  emitLog(
    jobId,
    skipSyn
      ? "[DWI] Registration: real ANTs antsRegistration (Affine only — SyN skipped; segmentation and reporting use the affine space)."
      : "[DWI] Registration: real ANTs antsRegistration (Affine+SyN) + antsApplyTransforms — single-threaded, this will take several minutes.",
  );
  const reg = await runDwiRegistration(
    prep.vol,
    adcVol,
    prep.mask,
    (stage) => emitLog(jobId, `[DWI]   registration: ${stage}`),
    rawB0 ?? undefined,
    { skipSyn },
  );

  const root = `${jobId}/DWI/registration`;
  const write = async (name: string, vol: NiftiVolume) =>
    outputStore.set(`${root}/${name}`, { blob: new Blob([(await niftiGz(vol)) as BlobPart], { type: "application/gzip" }), kind: "nifti" });
  await write(`${subjectId}_DWI_space-MNI152_aff.nii.gz`, reg.affineDwi);
  await write(`${subjectId}_ADC_space-MNI152_aff.nii.gz`, reg.affineAdc);
  if (reg.registeredDwi && reg.registeredAdc && reg.registeredMask) {
    await write(`${subjectId}_DWI_space-MNI152_affsyn.nii.gz`, reg.registeredDwi);
    await write(`${subjectId}_ADC_space-MNI152_affsyn.nii.gz`, reg.registeredAdc);
    await write(
      `${subjectId}_DWIbrain-mask_space-MNI152_affsyn.nii.gz`,
      maskToVol(reg.registeredMask, reg.registeredDwi.dims, reg.registeredDwi.pixdim, reg.registeredDwi.affine),
    );
  }
  // Real native also saves the affine-space mask on its own (not just the
  // affsyn QC copy above) — this is in fact the mask the real pipeline's own
  // segmentation stage reads (`DWIbrain-mask_space-MNI152_aff.nii.gz`, see
  // `run_ads_dwi.py:282`), already computed as `affineMaskBinary`, just not
  // previously saved to outputStore.
  await write(
    `${subjectId}_DWIbrain-mask_space-MNI152_aff.nii.gz`,
    maskToVol(reg.affineMaskBinary, reg.affineDwi.dims, reg.affineDwi.pixdim, reg.affineDwi.affine),
  );
  // B0 registered the same way as DWI/ADC (real `registration_align.py`'s
  // shared images dict) — output-completeness only, nothing downstream reads it.
  if (reg.affineB0) await write(`${subjectId}_B0_space-MNI152_aff.nii.gz`, reg.affineB0);
  if (reg.registeredB0) await write(`${subjectId}_B0_space-MNI152_affsyn.nii.gz`, reg.registeredB0);
  // desc-norm variants (percentile-clip[1,99]-rescale then z-score-in-mask —
  // same real normalization `pwi-pipeline.ts`'s descNorm already applies for
  // PWI's own affine-space inputs, reused verbatim here, not reimplemented).
  const descNorm = (vol: NiftiVolume): NiftiVolume => ({
    ...vol,
    voxels: zscoreInMask(percentileClipRescaleInMask(vol.voxels, reg.affineMaskBinary), reg.affineMaskBinary),
  });
  await write(`${subjectId}_DWI_space-MNI152_aff_desc-norm.nii.gz`, descNorm(reg.affineDwi));
  await write(`${subjectId}_ADC_space-MNI152_aff_desc-norm.nii.gz`, descNorm(reg.affineAdc));
  // Forward SyN warp field — already produced by the SyN call inside
  // runDwiRegistration, just not previously surfaced/saved.
  if (reg.synWarpField) {
    outputStore.set(`${root}/${subjectId}_warp_space-MNI1522MNI152.nii.gz`, {
      blob: new Blob([reg.synWarpField], { type: "application/gzip" }),
      kind: "nifti",
    });
  }
  outputStore.set(`${root}/${subjectId}_aff_space-individual2MNI152.mat`, {
    blob: new Blob([reg.dwiAffineTransform]),
    kind: "other",
  });
  if (reg.synAffineTransform) {
    outputStore.set(`${root}/${subjectId}_syn_space-MNI1522MNI152.mat`, {
      blob: new Blob([reg.synAffineTransform]),
      kind: "other",
    });
  }
  // Inverse transforms. Real `transform_io.py`'s `save_transforms()`: for
  // the 'aff' stage, `invtransforms` from ANTsPy's own registration() call
  // is the SAME forward GenericAffine.mat file (an affine's inverse is
  // applied algebraically at use-time via an invert flag, not a separately
  // computed matrix) — saving a second copy under the native invaff/invsyn
  // filenames for output parity, not a new computation. The inverse warp
  // IS a genuinely distinct file (`InverseWarp.nii.gz`), a direct, intrinsic
  // byproduct of SyN's symmetric-normalization optimization that our worker
  // already produces (see `synInverseWarpField`'s own doc comment).
  outputStore.set(`${root}/${subjectId}_invaff_space-MNI1522individual.mat`, {
    blob: new Blob([reg.dwiAffineTransform]),
    kind: "other",
  });
  if (reg.synAffineTransform) {
    outputStore.set(`${root}/${subjectId}_invsyn_space-MNI1522MNI152.mat`, {
      blob: new Blob([reg.synAffineTransform]),
      kind: "other",
    });
  }
  if (reg.synInverseWarpField) {
    outputStore.set(`${root}/${subjectId}_invwarp_space-MNI1522MNI152.nii.gz`, {
      blob: new Blob([reg.synInverseWarpField], { type: "application/gzip" }),
      kind: "nifti",
    });
  }
  // Warped ground-truth QC files — real native only produces these when a
  // reference stroke mask was actually supplied (true for the bundled demo
  // subject via "Load example subject", not for a real clinical run with no
  // label). Reuses the exact nearest-neighbor apply-transform pattern this
  // project's own DWI Dice verification already proved correct.
  const strokeFile = files.find((f) => classifyModality(f.name) === "Stroke");
  if (strokeFile) {
    try {
      const gtNative = await loadNiftiFile(strokeFile);
      const warped = await warpGroundTruthToMni(gtNative, reg, (s) => emitLog(jobId, `[DWI]   gt-warp: ${s}`));
      await write(`${subjectId}_stroke_space-MNI152_aff.nii.gz`, warped.aff);
      gtAffByJob.set(jobId, warped.aff);
      if (warped.affsyn) await write(`${subjectId}_stroke_space-MNI152_affsyn.nii.gz`, warped.affsyn);
    } catch (err) {
      emitLog(jobId, `[DWI] Ground-truth warp (QC only, non-fatal): ${(err as Error).message}`, "warn");
    }
  }

  // Real registration_manifest — a WASM-appropriate subset of the real
  // ManifestService's structure (`ads/services/manifest.py`): subject,
  // timestamp, and the real output paths this stage actually wrote. No
  // content hashing (the real manifest's `hashes` field) this pass — not
  // implemented, not faked. Fields with no browser equivalent (python
  // version, antsPy version) are simply omitted rather than faked.
  const synRan = !reg.synSkipped;
  const manifestOutputPaths = [
    `${subjectId}_DWI_space-MNI152_aff.nii.gz`, `${subjectId}_ADC_space-MNI152_aff.nii.gz`,
    `${subjectId}_DWIbrain-mask_space-MNI152_aff.nii.gz`,
    `${subjectId}_DWI_space-MNI152_aff_desc-norm.nii.gz`, `${subjectId}_ADC_space-MNI152_aff_desc-norm.nii.gz`,
    `${subjectId}_aff_space-individual2MNI152.mat`, `${subjectId}_invaff_space-MNI1522individual.mat`,
    ...(synRan
      ? [
          `${subjectId}_DWI_space-MNI152_affsyn.nii.gz`, `${subjectId}_ADC_space-MNI152_affsyn.nii.gz`,
          `${subjectId}_DWIbrain-mask_space-MNI152_affsyn.nii.gz`, `${subjectId}_warp_space-MNI1522MNI152.nii.gz`,
          `${subjectId}_syn_space-MNI1522MNI152.mat`, `${subjectId}_invsyn_space-MNI1522MNI152.mat`,
        ]
      : []),
    ...(reg.synInverseWarpField ? [`${subjectId}_invwarp_space-MNI1522MNI152.nii.gz`] : []),
    ...(strokeFile ? [`${subjectId}_stroke_space-MNI152_aff.nii.gz`] : []),
    ...(strokeFile && synRan ? [`${subjectId}_stroke_space-MNI152_affsyn.nii.gz`] : []),
  ];
  const manifest = {
    subject_id: subjectId,
    timestamp: new Date().toISOString(),
    engine: "openads-wasm (browser)",
    outputs: manifestOutputPaths,
    config: {
      registration: synRan
        ? "Affine (moving=brain mask) -> apply DWI/ADC -> SyN (moving=affine ADC) -> apply DWI/mask"
        : "Affine (moving=brain mask) -> apply DWI/ADC; SyN skipped (affsyn outputs fall back to aff)",
    },
  };
  outputStore.set(`${root}/${subjectId}_registration_manifest.json`, {
    blob: new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" }),
    kind: "json",
  });
  putText(`${root}/${subjectId}_registration_manifest.yaml`, toYaml(manifest), "text/yaml");

  emitLog(jobId, `[DWI] ✓ Registration completed — real ANTs ${synRan ? "Affine+SyN" : "Affine (SyN skipped)"} output`);
  emitStage(jobId, "DWI", "registration", "completed");
  return reg;
}

/** Real DAGMNet DWI (ch3) ONNX lesion segmentation (DWI only this pass).
 *  Runs on the MNI-AFFINE (not affsyn) registration output — confirmed
 *  against the real CLI's `SegmentationInputs` construction, see
 *  `runDwiSegmentation`'s doc comment in `lib/dwi-pipeline.ts` for the full
 *  citation. Output filename matches the real primary prediction's name
 *  (`{id}_stroke-mask_space-MNI152.nii.gz`, no space suffix) per
 *  `segmentation_paths.py`. */
async function runDwiInferenceStage(
  jobId: string,
  subjectId: string,
  reg: Awaited<ReturnType<typeof runDwiRegistration>>,
  dwiPrep?: { vol: NiftiVolume; mask: Uint8Array } | null,
  files: File[] = [],
) {
  emitStage(jobId, "DWI", "inference", "running");
  emitLog(jobId, "[DWI] Lesion Seg: running DAGMNet DWI (ch3) ONNX, 16-pass sliding window (MNI-affine space)…");
  const result = await runDwiSegmentation(reg.affineDwi, reg.affineAdc, reg.affineMaskBinary, (stage) =>
    emitLog(jobId, `[DWI]   dagmnet-dwi: ${stage}`),
  );
  const maskVol = maskToVol(result.mask, result.dims, reg.affineDwi.pixdim, reg.affineDwi.affine);
  outputStore.set(`${jobId}/DWI/segment/${subjectId}_stroke-mask_space-MNI152.nii.gz`, {
    blob: new Blob([(await niftiGz(maskVol)) as BlobPart], { type: "application/gzip" }),
    kind: "nifti",
  });
  emitLog(jobId, `[DWI] ✓ Lesion Seg completed — ${result.lesionVoxels.toLocaleString()} lesion voxels, ${result.totalMs}ms`);

  // Native-space prediction (`_stroke-mask.nii.gz`): the MNI-affine mask
  // pulled back onto the DWI grid by inverting the individual->MNI affine.
  let nativeMask: Uint8Array | null = null;
  if (dwiPrep) {
    try {
      const back = await runAntsApplyTransform(
        {
          voxels: Float32Array.from(result.mask),
          dims: result.dims,
          pixdim: reg.affineDwi.pixdim,
          affine: reg.affineDwi.affine,
          transforms: [{ path: "/dwi_aff.mat", bytes: reg.dwiAffineTransform, invert: true }],
          fixedTemplate: "affine",
          fixedOverride: { voxels: dwiPrep.vol.voxels, dims: dwiPrep.vol.dims, pixdim: dwiPrep.vol.pixdim, affine: dwiPrep.vol.affine },
          interpolator: "nearestNeighbor",
        },
        (s) => emitLog(jobId, `[DWI]   mni2orig: ${s}`),
      );
      nativeMask = new Uint8Array(back.registered.length);
      for (let i = 0; i < nativeMask.length; i++) nativeMask[i] = back.registered[i] > 0.5 ? 1 : 0;
      await putMask(`${jobId}/DWI/segment/${subjectId}_stroke-mask.nii.gz`, nativeMask, dwiPrep.vol);
    } catch (err) {
      emitLog(jobId, `[DWI] ⚠ Native-space prediction failed (MNI result unaffected): ${(err as Error).message}`, "warn");
    }
  }

  // metrics.json, as native `_compute_all_metrics`: MNI and native space,
  // each raw and after PostProcessingService.
  const strokeFile = files.find((f) => classifyModality(f.name) === "Stroke" && fileType(f.name) === "NIfTI");
  const gtMni = gtAffByJob.get(jobId)?.voxels ?? null;
  const gtOrig = strokeFile ? (await loadNiftiFile(strokeFile)).voxels : null;
  const metrics = {
    mni: segMetrics(result.mask, gtMni),
    orig: segMetrics(nativeMask, gtOrig),
    mni_postproc: segMetrics(postprocessDwi(result.mask, result.dims), gtMni),
    orig_postproc: segMetrics(nativeMask && dwiPrep ? postprocessDwi(nativeMask, dwiPrep.vol.dims) : null, gtOrig),
  };
  putText(`${jobId}/DWI/segment/${subjectId}_metrics.json`, JSON.stringify(metrics, null, 4), "application/json");
  if (metrics.mni.dice != null) emitLog(jobId, `[DWI]   metrics: Dice ${metrics.mni.dice.toFixed(4)} (MNI), ${metrics.orig.dice?.toFixed(4) ?? "—"} (native)`);

  emitStage(jobId, "DWI", "inference", "completed");
  return result;
}

/** Real atlas overlap / lesion volume / RandomForest region-interpretation
 *  reporting (DWI only this pass). Lesion mask is MNI-affine space (see
 *  `runDwiInferenceStage`), so the brain mask fed here for ICV/scoping must
 *  be the matching affine-space mask, not the SyN one. */
/** Saves per-atlas QFV/lesionload CSVs + the plain-text report to
 *  `outputStore`, matching the real native file set (see `report/qfv.ts`/
 *  `report/textReport.ts`). `stems` supplies the real per-modality file-stem
 *  casing: DWI uses `VASCULAR`/`LOBE`/`ASPECTS` (uppercase, `_QFV`/
 *  `_lesionload`); PWI uses `Vascular`/`Lobe`/`ASPECTS` (mixed case, `_HPQFV`/
 *  `_HPload`) — confirmed against real files on disk, not guessed. */
function saveReportFileSet(
  root: string,
  subjectId: string,
  fileSet: ReturnType<typeof buildReportFileSet>,
  stems: { vascular: string; lobe: string; aspects: string; qfvSuffix: string; lesionloadSuffix: string },
  textReportName: string,
) {
  const csv = (name: string, content: string) =>
    outputStore.set(`${root}/${name}`, { blob: new Blob([content], { type: "text/csv" }), kind: "other" });
  csv(`${subjectId}_${stems.vascular}_${stems.qfvSuffix}.csv`, fileSet.vascularQfvCsv);
  csv(`${subjectId}_${stems.vascular}_${stems.lesionloadSuffix}.csv`, fileSet.vascularLesionloadCsv);
  csv(`${subjectId}_${stems.lobe}_${stems.qfvSuffix}.csv`, fileSet.lobeQfvCsv);
  csv(`${subjectId}_${stems.lobe}_${stems.lesionloadSuffix}.csv`, fileSet.lobeLesionloadCsv);
  csv(`${subjectId}_${stems.aspects}_${stems.qfvSuffix}.csv`, fileSet.aspectsQfvCsv);
  csv(`${subjectId}_${stems.aspects}_${stems.lesionloadSuffix}.csv`, fileSet.aspectsLesionloadCsv);
  // Atlases native writes under the same stem for DWI and PWI.
  if (fileSet.aspectsPcQfvCsv) csv(`${subjectId}_ASPECTSPC_${stems.qfvSuffix}.csv`, fileSet.aspectsPcQfvCsv);
  if (fileSet.aspectsPcLesionloadCsv) csv(`${subjectId}_ASPECTSPC_${stems.lesionloadSuffix}.csv`, fileSet.aspectsPcLesionloadCsv);
  if (fileSet.bpmType1QfvCsv) csv(`${subjectId}_BPM_TYPE1_${stems.qfvSuffix}.csv`, fileSet.bpmType1QfvCsv);
  if (fileSet.bpmType1LesionloadCsv) csv(`${subjectId}_BPM_TYPE1_${stems.lesionloadSuffix}.csv`, fileSet.bpmType1LesionloadCsv);
  if (fileSet.ventriclesQfvCsv) csv(`${subjectId}_VENTRICLES_${stems.qfvSuffix}.csv`, fileSet.ventriclesQfvCsv);
  if (fileSet.ventriclesLesionloadCsv) csv(`${subjectId}_VENTRICLES_${stems.lesionloadSuffix}.csv`, fileSet.ventriclesLesionloadCsv);
  outputStore.set(`${root}/${subjectId}_${textReportName}.txt`, {
    blob: new Blob([fileSet.textReport], { type: "text/plain" }),
    kind: "other",
  });
}

async function runDwiReportStage(
  jobId: string,
  subjectId: string,
  lesion: { mask: Uint8Array; dims: number[] },
  reg: Awaited<ReturnType<typeof runDwiRegistration>>,
) {
  emitStage(jobId, "DWI", "report", "running");
  emitLog(jobId, "[DWI] Reporting: computing atlas overlap (vascular/lobe/ASPECTS) + lesion volume…");
  const lesionProb = new Float32Array(lesion.mask.length);
  for (let i = 0; i < lesionProb.length; i++) lesionProb[i] = lesion.mask[i];
  const { data, html } = await generateReport({
    subjectId,
    lesionProb,
    dims: lesion.dims as [number, number, number],
    pixdim: reg.affineDwi.pixdim,
    brainMask: reg.affineMaskBinary,
    // Ventricle features + hydrocephalus call (z-scored, so ADC scale is irrelevant).
    adcMni: reg.affineAdc.voxels,
    usingRealRegistration: true,
  });
  const root = `${jobId}/DWI/reporting`;
  void html; // native writes no HTML report; the browser report stays in memory only
  const fileSet = buildReportFileSet(data, "DWI");
  saveReportFileSet(
    root,
    subjectId,
    fileSet,
    { vascular: "VASCULAR", lobe: "LOBE", aspects: "ASPECTS", qfvSuffix: "QFV", lesionloadSuffix: "lesionload" },
    "automatic_radiological_report",
  );
  // `*_report_interpretation.pdf`: SHAP force plots of the region models,
  // fed the QFV rows exactly as native `gen_report_interpretation` does.
  try {
    const pdfs = await buildInterpretationPdfs({
      subjectId,
      features: {
        VASCULAR: qfvCsvToFeatureMap(fileSet.vascularQfvCsv),
        LOBE: qfvCsvToFeatureMap(fileSet.lobeQfvCsv),
        ASPECTS: qfvCsvToFeatureMap(fileSet.aspectsQfvCsv),
        ...(fileSet.aspectsPcQfvCsv ? { ASPECTSPC: qfvCsvToFeatureMap(fileSet.aspectsPcQfvCsv) } : {}),
      },
    });
    for (const [atlas, blob] of Object.entries(pdfs)) {
      if (blob) putFile(`${root}/${subjectId}_${atlas}_report_interpretation.pdf`, blob, "pdf");
    }
  } catch (err) {
    emitLog(jobId, `[DWI] Interpretation PDFs (non-fatal): ${(err as Error).message}`, "warn");
  }
  const interpNote =
    data.interpretation.status === "ok"
      ? "region classification (28 real RandomForest AA_models) included"
      : `region classification unavailable: ${data.interpretation.error ?? "unknown error"}`;
  emitLog(
    jobId,
    `[DWI] ✓ Reporting completed — total lesion volume ${data.volumes.totalMl.toFixed(2)}mL (L ${data.volumes.leftMl.toFixed(2)}mL / R ${data.volumes.rightMl.toFixed(2)}mL); ${interpNote}.`,
  );
  emitStage(jobId, "DWI", "report", "completed");
}

/** Runs DWI's skull-strip + registration internally, as a prerequisite for
 *  PWI's own registration — only when they haven't already run in this job
 *  (e.g. a combined DWI+PWI job already ran them as real DWI stages). Looks
 *  for DWI/ADC files in the same subject folder PWI's files came from. */
async function ensureDwiPrereqForPwi(
  jobId: string,
  subjectId: string,
  files: File[],
  existing: { prep: { vol: NiftiVolume; mask: Uint8Array } | null; reg: Awaited<ReturnType<typeof runDwiRegistration>> | null },
): Promise<{ prep: { vol: NiftiVolume; mask: Uint8Array }; reg: Awaited<ReturnType<typeof runDwiRegistration>> } | null> {
  if (existing.prep && existing.reg) return { prep: existing.prep, reg: existing.reg };
  const dwiFile = files.find((f) => classifyModality(f.name) === "DWI");
  const adcFile = files.find((f) => classifyModality(f.name) === "ADC");
  if (!dwiFile || !adcFile) {
    emitLog(jobId, "[PWI] Registration: needs DWI's own registration first, but no DWI/ADC files found in this subject folder — skipping", "warn");
    return null;
  }
  emitLog(jobId, "[PWI] Registration: running DWI's Skull Strip + Registration first (registration_pwi_align.py's real dependency — both modalities register into the same DWI-derived MNI space)");
  const prep = existing.prep ?? (await runSkullStrip(jobId, "DWI", dwiFile, subjectId));
  if (!prep) {
    emitLog(jobId, "[PWI] Registration: DWI prerequisite Skull Strip failed — skipping", "warn");
    return null;
  }
  const adcVol = await loadNiftiFile(adcFile);
  const reg = existing.reg ?? (await runDwiRegistrationStage(jobId, subjectId, prep, adcVol, undefined, files));
  return { prep, reg };
}

/** Real TTP generation (slice-time correction -> Gd concentration -> peak detection). */
async function runPwiTtpStage(jobId: string, subjectId: string, pwiFile: File, jsonFile: File | null, files: File[] = []) {
  emitStage(jobId, "PWI", "gen_ttp", "running");
  emitLog(jobId, "[PWI] TTP: real slice-time correction + Gd concentration + per-voxel peak detection — this can take a minute for a full series.");
  const result = await runPwiTtp(pwiFile, subjectId, jsonFile, (s) => emitLog(jobId, `[PWI]   ttp: ${s}`));
  const ttpVol: NiftiVolume = { dims: result.ttpDims, pixdim: result.ttpPixdim, affine: result.ttpAffine, voxels: result.ttpMap, datatype: 16 };
  outputStore.set(`${jobId}/PWI/preprocess/${subjectId}_TTP.nii.gz`, {
    blob: new Blob([(await niftiGz(ttpVol)) as BlobPart], { type: "application/gzip" }),
    kind: "nifti",
  });
  const pp = `${jobId}/PWI/preprocess`;
  // Native keeps the inputs it ran on and the series' temporal mean beside the TTP.
  putFile(`${pp}/${subjectId}_PWI.nii.gz`, pwiFile, "nifti");
  if (jsonFile) putFile(`${pp}/${subjectId}_PWI.json`, jsonFile, "json");
  const hpFile = files.find((f) => classifyModality(f.name) === "HP" && /manual/i.test(f.name) && fileType(f.name) === "NIfTI");
  if (hpFile) putFile(`${pp}/${subjectId}_HP_manual.nii.gz`, hpFile, "nifti");
  await putVol(`${pp}/${subjectId}_PWI_average.nii.gz`, result.pwiMaskRef);
  await putMask(`${pp}/${subjectId}_PWIbrain-mask.nii.gz`, result.pwiMask, result.pwiMaskRef);
  emitLog(jobId, `[PWI] ✓ TTP completed — ${result.ttpDims.join("x")} map, ${result.totalMs}ms`);
  emitStage(jobId, "PWI", "gen_ttp", "completed");
  return result;
}

/** Real two-step PWI registration (coreg to DWI-native, then DWI's own
 *  native->MNI affine reused) + desc-norm. `ttpadc_coreg` is folded into
 *  this stage (its own step 1), matching the real reference's single
 *  `runPwiRegistrationStage()`. */
async function runPwiRegistrationStage(
  jobId: string,
  subjectId: string,
  ttp: Awaited<ReturnType<typeof runPwiTtp>>,
  dwiPrep: { vol: NiftiVolume; mask: Uint8Array },
  dwiReg: Awaited<ReturnType<typeof runDwiRegistration>>,
  dwiRaw: PreparedDwiRaw | null = null,
  files: File[] = [],
) {
  emitStage(jobId, "PWI", "registration", "running");
  emitLog(jobId, "[PWI] Registration: real ANTs antsRegistration (Affine, PWI mask -> DWI native mask) + antsApplyTransforms (2x) — reuses DWI's own Registration output.");
  const reg = await runPwiRegistration(ttp, dwiPrep, dwiReg, (s) => emitLog(jobId, `[PWI]   registration: ${s}`));

  const root = `${jobId}/PWI/registration`;
  const write = async (name: string, vol: NiftiVolume) =>
    outputStore.set(`${root}/${name}`, { blob: new Blob([(await niftiGz(vol)) as BlobPart], { type: "application/gzip" }), kind: "nifti" });
  await write(`${subjectId}_TTP_space-MNI152_aff.nii.gz`, reg.ttpAff);
  await write(`${subjectId}_DWI_space-MNI152_aff_desc-norm.nii.gz`, reg.dwiDescNorm);
  await write(`${subjectId}_ADC_space-MNI152_aff_desc-norm.nii.gz`, reg.adcDescNorm);
  await write(`${subjectId}_TTP_space-MNI152_aff_desc-norm.nii.gz`, reg.ttpDescNorm);
  if (reg.ttpOnDwiNative) await write(`${subjectId}_TTP_space-DWI.nii.gz`, reg.ttpOnDwiNative);
  if (reg.ttpOnDwiNativeBrain) await write(`${subjectId}_TTP_space-DWI_desc-brain.nii.gz`, reg.ttpOnDwiNativeBrain);

  // Native PWI/ keeps its own copy of everything DWI-derived it used.
  const dwiRoot = `${jobId}/DWI`;
  const pp = `${jobId}/PWI/preprocess`;
  for (const name of ["DWI", "ADC", "B0", "DWIbrain-mask", "stroke"]) {
    copyOutput(`${dwiRoot}/preprocess/${subjectId}_${name}.nii.gz`, `${pp}/${subjectId}_${name}.nii.gz`);
  }
  if (!outputStore.has(`${pp}/${subjectId}_DWIbrain-mask.nii.gz`)) await putMask(`${pp}/${subjectId}_DWIbrain-mask.nii.gz`, dwiPrep.mask, dwiPrep.vol);
  await writeDwiBrainCopies(jobId, "PWI", subjectId, dwiRaw, dwiPrep.mask, false);
  for (const name of [
    "DWI_space-MNI152_aff.nii.gz",
    "ADC_space-MNI152_aff.nii.gz",
    "DWIbrain-mask_space-MNI152_aff.nii.gz",
    "aff_space-individual2MNI152.mat",
    "invaff_space-MNI1522individual.mat",
  ]) {
    copyOutput(`${dwiRoot}/registration/${subjectId}_${name}`, `${root}/${subjectId}_${name}`);
  }
  putFile(`${root}/${subjectId}_aff_space-individualTTP2ADC.mat`, new Blob([reg.coregAffine]), "other");
  putFile(`${root}/${subjectId}_invaff_space-ADC2individualTTP.mat`, new Blob([reg.coregAffine]), "other");

  // Reference HP (manual label, native PWI grid) into DWI space and MNI-affine.
  const hpFile = files.find((f) => classifyModality(f.name) === "HP" && /manual/i.test(f.name) && fileType(f.name) === "NIfTI");
  if (hpFile) {
    try {
      const hp = await loadNiftiFile(hpFile);
      const toDwi = await runAntsApplyTransform(
        {
          voxels: hp.voxels,
          dims: hp.dims,
          pixdim: hp.pixdim,
          affine: hp.affine,
          transforms: [{ path: "/coreg_aff.mat", bytes: reg.coregAffine }],
          fixedTemplate: "affine",
          fixedOverride: { voxels: dwiPrep.vol.voxels, dims: dwiPrep.vol.dims, pixdim: dwiPrep.vol.pixdim, affine: dwiPrep.vol.affine },
          interpolator: "nearestNeighbor",
        },
        (s) => emitLog(jobId, `[PWI]   hp-manual->DWI: ${s}`),
      );
      const hpDwi: NiftiVolume = { dims: toDwi.dims, pixdim: toDwi.pixdim, affine: toDwi.affine, voxels: toDwi.registered, datatype: 16 };
      const hpDwiBrain = maskedVol(hpDwi, dwiPrep.mask);
      await write(`${subjectId}_HP_manual_space-DWI.nii.gz`, hpDwi);
      await write(`${subjectId}_HP_manual_space-DWI_desc-brain.nii.gz`, hpDwiBrain);
      const toMni = await runAntsApplyTransform(
        {
          voxels: hpDwiBrain.voxels,
          dims: hpDwiBrain.dims,
          pixdim: hpDwiBrain.pixdim,
          affine: hpDwiBrain.affine,
          transforms: [{ path: "/dwi_aff.mat", bytes: dwiReg.dwiAffineTransform }],
          fixedTemplate: "affine",
          interpolator: "nearestNeighbor",
        },
        (s) => emitLog(jobId, `[PWI]   hp-manual->MNI: ${s}`),
      );
      const hpMni: NiftiVolume = { dims: toMni.dims, pixdim: toMni.pixdim, affine: toMni.affine, voxels: toMni.registered, datatype: 16 };
      await write(`${subjectId}_HP_manual_space-MNI152_aff.nii.gz`, hpMni);
      hpGtByJob.set(jobId, { native: hp.voxels, mni: hpMni.voxels });
    } catch (err) {
      emitLog(jobId, `[PWI] Reference HP warp (QC only, non-fatal): ${(err as Error).message}`, "warn");
    }
  }

  emitLog(jobId, "[PWI] ✓ Registration completed — real two-step coregistration + desc-norm output");
  emitStage(jobId, "PWI", "registration", "completed");
  emitStage(jobId, "PWI", "ttpadc_coreg", "completed"); // folded into step 1 above, see header comment
  return reg;
}

/** Real DAGMNet PWI (ch4) ONNX hypoperfusion segmentation, plus
 *  `restore_hp_mni2orig` (native-space restoration). */
async function runPwiInferenceStage(
  jobId: string,
  subjectId: string,
  reg: Awaited<ReturnType<typeof runPwiRegistration>>,
  dwiReg: Awaited<ReturnType<typeof runDwiRegistration>>,
  dwiPrep: { vol: NiftiVolume; mask: Uint8Array },
  pwiTtp: Awaited<ReturnType<typeof runPwiTtp>>,
) {
  emitStage(jobId, "PWI", "inference", "running");
  emitLog(jobId, "[PWI] Lesion Seg: running DAGMNet PWI (ch4) ONNX, 16-pass sliding window — HPsymclassic channel zero-filled (see pwi-pipeline.ts header: a real SLIC-based generator exists but regressed Dice when live-tested).");
  const result = await runPwiSegmentation(reg, (s) => emitLog(jobId, `[PWI]   dagmnet-pwi: ${s}`));
  const maskVol = maskToVol(result.mask, result.dims, reg.dwiDescNorm.pixdim, reg.dwiDescNorm.affine);
  outputStore.set(`${jobId}/PWI/segmentation/${subjectId}_HP-mask_space-MNI152.nii.gz`, {
    blob: new Blob([(await niftiGz(maskVol)) as BlobPart], { type: "application/gzip" }),
    kind: "nifti",
  });
  emitLog(jobId, `[PWI] ✓ Lesion Seg completed — ${result.lesionVoxels.toLocaleString()} lesion voxels, ${result.totalMs}ms`);

  let nativeHp: Uint8Array | null = null;
  emitLog(jobId, "[PWI] Restoring hypoperfusion mask to native space (restore_hp_mni2orig: MNI-affine -> DWI/ADC-native -> PWI native TTP space, both by inverting an already-computed forward transform)…");
  try {
    const native = await restorePwiHpToNativeSpace(result, dwiReg, reg, dwiPrep, pwiTtp, (s) => emitLog(jobId, `[PWI]   restore-native: ${s}`));
    const nativeVol = maskToVol(native.mask, native.dims, native.pixdim, native.affine);
    outputStore.set(`${jobId}/PWI/segmentation/${subjectId}_HP-mask.nii.gz`, {
      blob: new Blob([(await niftiGz(nativeVol)) as BlobPart], { type: "application/gzip" }),
      kind: "nifti",
    });
    nativeHp = native.mask;
    emitLog(jobId, `[PWI] ✓ Native-space restoration completed`);
  } catch (err) {
    emitLog(jobId, `[PWI] ⚠ Native-space restoration failed (MNI-space result above is unaffected): ${(err as Error).message}`, "warn");
  }

  // `_HP-mask_space-MNI152_desc-postproc` (postprocess_hp_mask, brain-masked
  // with DWI's MNI-affine mask) and metrics.json against the reference HP.
  const postproc = postprocessHp(result.mask, result.dims, dwiReg.affineMaskBinary);
  await putMask(`${jobId}/PWI/segmentation/${subjectId}_HP-mask_space-MNI152_desc-postproc.nii.gz`, postproc, reg.dwiDescNorm);
  const gt = hpGtByJob.get(jobId);
  const metrics = {
    mni: segMetrics(result.mask, gt?.mni ?? null),
    orig: segMetrics(nativeHp, gt?.native ?? null),
    mni_postproc: segMetrics(postproc, gt?.mni ?? null),
    orig_postproc: segMetrics(nativeHp && pwiTtp ? postprocessDwi(nativeHp, pwiTtp.ttpDims) : null, gt?.native ?? null),
  };
  putText(`${jobId}/PWI/segmentation/${subjectId}_metrics.json`, JSON.stringify(metrics, null, 2), "application/json");
  if (metrics.mni.dice != null) emitLog(jobId, `[PWI]   metrics: Dice ${metrics.mni.dice.toFixed(4)} (MNI), ${metrics.orig.dice?.toFixed(4) ?? "—"} (native)`);

  emitStage(jobId, "PWI", "inference", "completed");
  return result;
}

/** Real atlas overlap / lesion volume / region-classification reporting
 *  (same shared `generateReport()` DWI uses — modality-agnostic, both land
 *  on the same 182x218x182 MNI grid). */
async function runPwiReportStage(jobId: string, subjectId: string, lesion: { mask: Uint8Array; dims: number[] }, reg: Awaited<ReturnType<typeof runPwiRegistration>>) {
  emitStage(jobId, "PWI", "report", "running");
  emitLog(jobId, "[PWI] Reporting: computing atlas overlap (vascular/lobe/ASPECTS) + lesion volume…");
  const lesionProb = new Float32Array(lesion.mask.length);
  for (let i = 0; i < lesionProb.length; i++) lesionProb[i] = lesion.mask[i];
  const { data, html } = await generateReport({
    subjectId,
    lesionProb,
    dims: lesion.dims as [number, number, number],
    pixdim: reg.dwiDescNorm.pixdim,
    usingRealRegistration: true,
  });
  const root = `${jobId}/PWI/reporting`;
  void html; // native writes no HTML report
  saveReportFileSet(
    root,
    subjectId,
    buildReportFileSet(data, "PWI"),
    { vascular: "Vascular", lobe: "Lobe", aspects: "ASPECTS", qfvSuffix: "HPQFV", lesionloadSuffix: "HPload" },
    "automatic_radiological_report",
  );
  emitLog(jobId, `[PWI] ✓ Reporting completed — total lesion volume ${data.volumes.totalMl.toFixed(2)}mL (L ${data.volumes.leftMl.toFixed(2)}mL / R ${data.volumes.rightMl.toFixed(2)}mL).`);
  emitStage(jobId, "PWI", "report", "completed");
}

/**
 * A PWI-only run still has to skull-strip and register the DWI (PWI
 * registration reuses DWI's MNI transform). Native keeps those files inside
 * `PWI/` and writes no `DWI/` folder, so move them there, dropping the ones
 * native's PWI tree does not have.
 */
function foldDwiPrereqIntoPwi(jobId: string): void {
  const prefix = `${jobId}/DWI/`;
  for (const key of [...outputStore.keys()]) {
    if (!key.startsWith(prefix)) continue;
    const rel = key.slice(prefix.length);
    const entry = outputStore.get(key)!;
    outputStore.delete(key);
    if (/registration_manifest|_B0_space-|_stroke_space-/.test(rel)) continue;
    const target = `${jobId}/PWI/${rel}`;
    if (!outputStore.has(target)) outputStore.set(target, entry);
  }
}

/** Reads one of this job's outputs back as a volume (null when it was not written). */
async function readOut(path: string): Promise<NiftiVolume | null> {
  const entry = outputStore.get(path);
  if (!entry) return null;
  try {
    return await loadNiftiFile(entry.blob);
  } catch {
    return null;
  }
}

/**
 * Native's report figures (`DWIstroke*.png`, `HP*.png`), drawn last from the
 * files the stages wrote under their native names.
 */
async function writeReportFigures(jobId: string, subjectId: string): Promise<void> {
  const j = (rel: string) => `${jobId}/${rel}`;
  const sid = subjectId;
  try {
    // ---- report figures
    const dwiPredMni = await readOut(j(`DWI/segment/${sid}_stroke-mask_space-MNI152.nii.gz`));
    if (dwiPredMni) {
      emitLog(jobId, "[DWI] Rendering report figures…");
      const native = {
        dwi: (await readOut(j(`DWI/preprocess/${sid}_DWI_brain.nii.gz`))) ?? (await readOut(j(`DWI/preprocess/${sid}_DWI.nii.gz`))),
        adc: (await readOut(j(`DWI/preprocess/${sid}_ADC_brain.nii.gz`))) ?? (await readOut(j(`DWI/preprocess/${sid}_ADC.nii.gz`))),
        label: await readOut(j(`DWI/preprocess/${sid}_stroke.nii.gz`)),
        pred: await readOut(j(`DWI/segment/${sid}_stroke-mask.nii.gz`)),
      };
      if (native.dwi && native.adc && native.pred) {
        putFile(j(`DWI/reporting/${sid}_DWIstroke.png`), await renderDwiStrokeFigure({ dwi: native.dwi, adc: native.adc, label: native.label, pred: native.pred, space: "native" }), "png");
      }
      const mniDwi = await readOut(j(`DWI/registration/${sid}_DWI_space-MNI152_aff.nii.gz`));
      const mniAdc = await readOut(j(`DWI/registration/${sid}_ADC_space-MNI152_aff.nii.gz`));
      if (mniDwi && mniAdc) {
        const label = await readOut(j(`DWI/registration/${sid}_stroke_space-MNI152_aff.nii.gz`));
        putFile(j(`DWI/reporting/${sid}_DWIstroke_space-MNI152.png`), await renderDwiStrokeFigure({ dwi: mniDwi, adc: mniAdc, label, pred: dwiPredMni, space: "MNI152" }), "png");
      }
    }
    const hpPredMni = await readOut(j(`PWI/segmentation/${sid}_HP-mask_space-MNI152.nii.gz`));
    if (hpPredMni) {
      emitLog(jobId, "[PWI] Rendering report figures…");
      const ttp = await readOut(j(`PWI/preprocess/${sid}_TTP.nii.gz`));
      const hpPred = await readOut(j(`PWI/segmentation/${sid}_HP-mask.nii.gz`));
      if (ttp && hpPred) {
        const hpLabel = await readOut(j(`PWI/preprocess/${sid}_HP_manual.nii.gz`));
        putFile(j(`PWI/reporting/${sid}_HP.png`), await renderHpFigure({ ttp, hpPred, hpLabel, space: "native" }), "png");
      }
      const ttpMni = await readOut(j(`PWI/registration/${sid}_TTP_space-MNI152_aff.nii.gz`));
      const dwiMni = await readOut(j(`PWI/registration/${sid}_DWI_space-MNI152_aff.nii.gz`));
      if (ttpMni && dwiMni) {
        putFile(
          j(`PWI/reporting/${sid}_HP_space-MNI152.png`),
          await renderHpFigure({
            ttp: ttpMni,
            hpPred: hpPredMni,
            hpLabel: await readOut(j(`PWI/registration/${sid}_HP_manual_space-MNI152_aff.nii.gz`)),
            dwi: dwiMni,
            strokeLabel: await readOut(j(`DWI/registration/${sid}_stroke_space-MNI152_aff.nii.gz`)),
            // Native draws the SyN-space stroke prediction here; none when SyN is skipped.
            strokePred: null,
            space: "MNI152",
          }),
          "png",
        );
      }
    }

  } catch (err) {
    emitLog(jobId, `[Report] Figures (non-fatal): ${(err as Error).message}`, "warn");
  }
}

export async function startJobWeb(spec: JobSpec): Promise<JobHandle> {
  const jobId = `job-${++jobSeq}`;
  const manifest = cases.get(spec.caseId);
  const dwiStages = spec.stages.dwi;
  const pwiStages = spec.stages.pwi;
  const stageList: StageStatus[] = [
    ...dwiStages.map((id): StageStatus => ({ id, modality: "DWI", title: stageTitle(id), state: "pending", startedIso: null, finishedIso: null, durationSec: null })),
    ...pwiStages.map((id): StageStatus => ({ id, modality: "PWI", title: stageTitle(id), state: "pending", startedIso: null, finishedIso: null, durationSec: null })),
  ];

  const status: JobStatus = {
    jobId,
    caseId: spec.caseId,
    subjectId: manifest?.subjectId ?? "unknown",
    state: "queued",
    containerId: null,
    startedIso: null,
    finishedIso: null,
    exitCode: null,
    progressPct: 0,
    currentStage: null,
    currentModality: null,
    stages: stageList,
    errorMessage: null,
    engine: "native",
    deviceLabel: "Browser · WASM",
    deviceClass: "cpu",
    health: null,
  };
  jobs.set(jobId, status);
  skipSynByJob.set(jobId, spec.configOverrides?.["registration.run_syn"] !== true);

  void (async () => {
    status.state = "running";
    status.startedIso = nowIso();
    emitState(jobId, "running");
    emitLog(jobId, `Starting run in-browser (WASM engine, no Docker/Python) — subject ${status.subjectId}`);

    // Per-job state, threaded between stages exactly like
    // `openads-wasm/app/src/main.ts`'s single global `state.stage` object —
    // scoped to this job's closure instead, since engine.ts can have
    // multiple jobs' worth of history alive across a session.
    let dwiRawPrep: PreparedDwiRaw | null = null;
    let dwiPrep: { vol: NiftiVolume; mask: Uint8Array } | null = null;
    let dwiReg: Awaited<ReturnType<typeof runDwiRegistration>> | null = null;
    let dwiLesion: { mask: Uint8Array; dims: number[] } | null = null;
    let pwiTtp: Awaited<ReturnType<typeof runPwiTtp>> | null = null;
    let pwiReg: Awaited<ReturnType<typeof runPwiRegistration>> | null = null;
    let pwiLesion: { mask: Uint8Array; dims: number[] } | null = null;
    const subjectId = status.subjectId;

    try {
      for (const stage of stageList) {
        status.currentStage = stage.id;
        status.currentModality = stage.modality;
        const subjectDir = manifest?.inputRoot ?? "";
        const files = isBrowserSubjectDir(subjectDir) ? filesFor(subjectDir) : [];

        if (stage.id === "prepdata") {
          if (stage.modality === "DWI") {
            dwiRawPrep = await runDwiPrepStage(jobId, "DWI", subjectId, files);
          } else {
            // PWI shares the same DWI/ADC/b0 prep (needed for its own
            // registration's DWI-reuse step) only if it hasn't already run
            // this job (e.g. a combined DWI+PWI job already prepped it).
            if (!dwiRawPrep) dwiRawPrep = await runDwiPrepStage(jobId, "PWI", subjectId, files);
            else emitStage(jobId, "PWI", "prepdata", "completed");
          }
        } else if (stage.id === "skull_strip" || stage.id === "gen_mask") {
          if (stage.id === "gen_mask") continue; // folded into skull_strip, see NOT_YET_WIRED
          const want = stage.modality === "DWI" ? "DWI" : "PWI";
          // E2E test patch: without the NIfTI check the PWI JSON sidecar was picked.
          const file = files.find((f) => classifyModality(f.name) === want && fileType(f.name) === "NIfTI");
          if (!file) {
            emitLog(jobId, `[${stage.modality}] No ${want} file found to skull-strip — skipping`, "warn");
            emitStage(jobId, stage.modality, stage.id, "skipped");
          } else {
            const preloaded = stage.modality === "DWI" ? (dwiRawPrep?.dwi ?? undefined) : undefined;
            const result = await runSkullStrip(jobId, stage.modality, file, subjectId, preloaded);
            if (result && stage.modality === "DWI") {
              dwiPrep = result;
              await writeDwiBrainCopies(jobId, "DWI", subjectId, dwiRawPrep, result.mask, true);
            }
          }
        } else if (stage.id === "registration" && stage.modality === "DWI") {
          const adcFile = files.find((f) => classifyModality(f.name) === "ADC");
          if (!dwiPrep) {
            emitLog(jobId, "[DWI] Registration: skipped — run Skull Strip first (Affine registers the brain mask, matching the real OpenADS pipeline)", "warn");
            emitStage(jobId, "DWI", "registration", "skipped");
          } else if (!dwiRawPrep?.adc && !adcFile) {
            emitLog(jobId, "[DWI] Registration: skipped — no ADC file found (real OpenADS registers ADC via SyN and DWI via antsApplyTransforms, so both are needed)", "warn");
            emitStage(jobId, "DWI", "registration", "skipped");
          } else {
            const adcVol = dwiRawPrep?.adc ?? (await loadNiftiFile(adcFile!));
            dwiReg = await runDwiRegistrationStage(jobId, subjectId, dwiPrep, adcVol, dwiRawPrep?.b0, files);
          }
        } else if (stage.id === "inference" && stage.modality === "DWI") {
          if (!dwiReg) {
            emitLog(jobId, "[DWI] Lesion Seg: skipped — run Registration first (Lesion Seg consumes its output)", "warn");
            emitStage(jobId, "DWI", "inference", "skipped");
          } else {
            dwiLesion = await runDwiInferenceStage(jobId, subjectId, dwiReg, dwiPrep, files);
          }
        } else if (stage.id === "report" && stage.modality === "DWI") {
          if (!dwiLesion || !dwiReg) {
            emitLog(jobId, "[DWI] Reporting: skipped — run Lesion Seg first (Reporting consumes its output)", "warn");
            emitStage(jobId, "DWI", "report", "skipped");
          } else {
            await runDwiReportStage(jobId, subjectId, dwiLesion, dwiReg);
          }
        } else if (stage.id === "gen_ttp") {
          const pwiFile = files.find((f) => classifyModality(f.name) === "PWI" && fileType(f.name) === "NIfTI");
          const jsonFile = files.find((f) => classifyModality(f.name) === "PWI" && fileType(f.name) === "JSON") ?? null;
          if (!pwiFile) {
            emitLog(jobId, "[PWI] TTP: skipped — no 4D PWI series found", "warn");
            emitStage(jobId, "PWI", "gen_ttp", "skipped");
          } else {
            pwiTtp = await runPwiTtpStage(jobId, subjectId, pwiFile, jsonFile, files);
          }
        } else if (stage.id === "registration" && stage.modality === "PWI") {
          if (!pwiTtp) {
            emitLog(jobId, "[PWI] Registration: skipped — run TTP first (needs the native PWI mask + TTP map)", "warn");
            emitStage(jobId, "PWI", "registration", "skipped");
            emitStage(jobId, "PWI", "ttpadc_coreg", "skipped");
          } else {
            const prereq = await ensureDwiPrereqForPwi(jobId, subjectId, files, { prep: dwiPrep, reg: dwiReg });
            if (!prereq) {
              emitStage(jobId, "PWI", "registration", "skipped");
              emitStage(jobId, "PWI", "ttpadc_coreg", "skipped");
            } else {
              dwiPrep = prereq.prep;
              dwiReg = prereq.reg;
              pwiReg = await runPwiRegistrationStage(jobId, subjectId, pwiTtp, prereq.prep, prereq.reg, dwiRawPrep, files);
            }
          }
        } else if (stage.id === "ttpadc_coreg") {
          continue; // folded into PWI registration, see runPwiRegistrationStage
        } else if (stage.id === "inference" && stage.modality === "PWI") {
          if (!pwiReg || !dwiReg || !dwiPrep || !pwiTtp) {
            emitLog(jobId, "[PWI] Lesion Seg: skipped — run Registration first (Lesion Seg consumes its output)", "warn");
            emitStage(jobId, "PWI", "inference", "skipped");
          } else {
            pwiLesion = await runPwiInferenceStage(jobId, subjectId, pwiReg, dwiReg, dwiPrep, pwiTtp);
          }
        } else if (stage.id === "report" && stage.modality === "PWI") {
          if (!pwiLesion || !pwiReg) {
            emitLog(jobId, "[PWI] Reporting: skipped — run Lesion Seg first (Reporting consumes its output)", "warn");
            emitStage(jobId, "PWI", "report", "skipped");
          } else {
            await runPwiReportStage(jobId, subjectId, pwiLesion, pwiReg);
          }
        } else {
          const reason = NOT_YET_WIRED[stage.id] ?? "Not wired into ADS Desk yet.";
          emitLog(jobId, `[${stage.modality}] — ${stage.title}: ${reason}`, "warn");
          emitStage(jobId, stage.modality, stage.id, "skipped");
        }
        emitProgress(jobId);
      }
      if (dwiStages.length === 0) foldDwiPrereqIntoPwi(jobId);
      await writeReportFigures(jobId, subjectId);
      emitLog(jobId, "✅ Run finished (see warnings above for any stages not wired into this browser build)");
      emitState(jobId, "completed");
    } catch (err) {
      emitState(jobId, "failed", (err as Error).message);
    }
  })();

  return { jobId, command: ["(in-browser WASM engine)"], logPath: "", engine: "native", deviceLabel: "Browser · WASM", deviceClass: "cpu" };
}

export function getJobWeb(jobId: string): JobStatus | null {
  return jobs.get(jobId) ?? null;
}

/** Every job this page has started, newest first — what `reconcileJob` checks live runs against. */
export function listJobsWeb(limit = 50): JobStatus[] {
  return [...jobs.values()].reverse().slice(0, limit);
}

// ------------------------------------------------------------- batch ---
/**
 * Real multi-subject batch processing, ported from the actual reference
 * orchestration (`openads-wasm/source/scripts/batch_run_combined.sh`):
 * scan a root for `sub-*` folders, run the existing single-subject
 * combined (DWI+PWI) job for each, sequentially, continuing past a failed
 * subject rather than aborting the whole batch (the bash script's own
 * `process_subject_pair` + loop-with-continue behavior) and reporting a
 * summary (total/success/failed/elapsed) at the end — the real script has
 * no cross-subject aggregation beyond that summary, so neither does this.
 *
 * Deliberately reuses `createCaseWeb`/`startJobWeb` rather than a parallel
 * execution path: a batch is "the same real single-subject job, run once
 * per detected subject," not a different pipeline.
 */
export interface BatchSubjectResult {
  subjectId: string;
  subjectDir: string;
  jobId: string | null;
  state: "success" | "failed";
  errorMessage: string | null;
  durationMs: number;
}

export interface BatchResult {
  total: number;
  success: number;
  failed: number;
  elapsedMs: number;
  subjects: BatchSubjectResult[];
}

/**
 * Pick a root folder containing multiple subject subfolders (e.g.
 * `root/sub-001/...`, `root/sub-002/...`) and group the picked files by
 * their top-level folder name — the same `browser://<id>` fileStore
 * convention `pickDirectoryWeb` already uses for a single subject, just
 * applied once per detected subject instead of once for the whole pick.
 */
export function pickBatchDirectoryWeb(): Promise<{ subjectDirs: string[] }> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    (input as HTMLInputElement & { webkitdirectory: boolean }).webkitdirectory = true;
    input.multiple = true;
    input.style.display = "none";
    input.addEventListener(
      "change",
      () => {
        const files = Array.from(input.files ?? []);
        document.body.removeChild(input);
        if (files.length === 0) {
          resolve({ subjectDirs: [] });
          return;
        }
        // Group by the folder immediately under the picked root. A picked
        // tree is `<root>/<subject>/...`, so that's path segment 1 (segment
        // 0 is the root folder itself, which webkitRelativePath always
        // includes).
        const bySubject = new Map<string, File[]>();
        for (const f of files) {
          const rel = (f as File & { webkitRelativePath?: string }).webkitRelativePath || "";
          const parts = rel.split("/");
          const subjectId = parts.length > 2 ? parts[1] : null;
          if (!subjectId) continue;
          if (!bySubject.has(subjectId)) bySubject.set(subjectId, []);
          bySubject.get(subjectId)!.push(f);
        }
        const subjectDirs: string[] = [];
        for (const [subjectId, subjectFiles] of bySubject) {
          const subjectDir = `browser://${subjectId}`;
          fileStore.set(subjectDir, subjectFiles);
          subjectDirs.push(subjectDir);
        }
        resolve({ subjectDirs: subjectDirs.sort() });
      },
      { once: true },
    );
    document.body.appendChild(input);
    input.click();
  });
}

function waitForJobWeb(jobId: string): Promise<JobStatus> {
  return new Promise((resolve) => {
    const poll = () => {
      const status = jobs.get(jobId);
      if (status && (status.state === "completed" || status.state === "failed" || status.state === "cancelled")) {
        resolve(status);
        return;
      }
      setTimeout(poll, 500);
    };
    poll();
  });
}

export async function runBatchWeb(
  subjectDirs: string[],
  opts: { pipelineKind: PipelineKind; stages: StageSelection; gpuIndex: string; dockerImage: string | null; cleanupEnabled: boolean },
): Promise<BatchResult> {
  const startedAt = Date.now();
  const subjects: BatchSubjectResult[] = [];

  for (const subjectDir of subjectDirs) {
    const subjectId = subjectDir.replace(/^browser:\/\//, "");
    const subjectStart = Date.now();
    // One subject's failure must not abort the batch — matching
    // batch_run_combined.sh's per-subject try/continue, not a fail-fast loop.
    try {
      const manifest = createCaseWeb({
        subjectDir,
        outputRoot: "browser://output",
        subjectId,
        studyName: subjectId,
        pipelineKind: opts.pipelineKind,
        pwiSubjectDir: subjectDir, // this app keeps both modalities' raw files together per subject, unlike the real script's separate DWI/PWI roots
        gpuIndex: opts.gpuIndex,
        dockerImage: opts.dockerImage,
        stages: opts.stages,
        cleanupEnabled: opts.cleanupEnabled,
      });
      const handle = await startJobWeb({
        caseId: manifest.caseId,
        pipelineKind: opts.pipelineKind,
        stages: opts.stages,
        gpuIndex: opts.gpuIndex,
        dockerImage: opts.dockerImage,
        cleanupEnabled: opts.cleanupEnabled,
      });
      const finalStatus = await waitForJobWeb(handle.jobId);
      subjects.push({
        subjectId,
        subjectDir,
        jobId: handle.jobId,
        state: finalStatus.state === "completed" ? "success" : "failed",
        errorMessage: finalStatus.errorMessage,
        durationMs: Date.now() - subjectStart,
      });
    } catch (err) {
      subjects.push({
        subjectId,
        subjectDir,
        jobId: null,
        state: "failed",
        errorMessage: (err as Error).message,
        durationMs: Date.now() - subjectStart,
      });
    }
  }

  const success = subjects.filter((s) => s.state === "success").length;
  return {
    total: subjects.length,
    success,
    failed: subjects.length - success,
    elapsedMs: Date.now() - startedAt,
    subjects,
  };
}

export function listOutputsWeb(_caseId: string): { root: string; tree: OutputNode[] } {
  const tree: OutputNode[] = [];
  // Was hardcoding kind: "other" regardless of what the stage that produced
  // the file actually recorded — meant every real .nii.gz output (incl. the
  // registration/segmentation files above) would never render in the
  // viewer, only show as a generic download.
  for (const [path, { blob, kind }] of outputStore) {
    tree.push({ name: path.split("/").pop() ?? path, relPath: path, isDir: false, sizeBytes: blob.size, modifiedIso: nowIso(), kind });
  }
  return { root: "browser://output", tree };
}

export function getAssetUrlWeb(path: string): { url: string } {
  // E2E test patch: the Output browser joins its root onto the relPath, so
  // also accept a key that sits under a "browser://..." prefix.
  const key = outputStore.has(path) ? path : [...outputStore.keys()].find((k) => path.endsWith(`/${k}`));
  const entry = key ? outputStore.get(key) : undefined;
  if (!entry) {
    // An input the reader opened: "browser://<subject>/<relPath>".
    for (const [dir, files] of fileStore) {
      if (!path.startsWith(`${dir}/`)) continue;
      const rel = path.slice(dir.length + 1);
      const file = files.find((f) => relPathOf(f) === rel);
      if (file) return { url: URL.createObjectURL(file) };
    }
    return { url: "" };
  }
  return { url: URL.createObjectURL(entry.blob) };
}

export function systemStatusWeb(): SystemStatus {
  return {
    engine: engineStatusWeb(),
    docker: { available: false, version: null, daemonRunning: false, error: "This is the browser build — no Docker, no server. Everything runs client-side in WASM.", images: [], selectedImage: null, gpuRuntimeAvailable: false },
    gpu: { available: false, devices: [], driverVersion: null, cudaVersion: null, error: null },
    storage: [],
    appVersion: "0.1.0-wasm",
    platform: "linux",
    concurrency: { limit: 1, automatic: 1, threadsPerJob: 1, reason: "ONNX Runtime Web runs single-threaded in this build (no cross-origin isolation); one run at a time." },
  };
}

export function engineStatusWeb(): EngineStatus {
  return {
    kind: "native",
    selection: "auto",
    ready: true,
    deviceClass: "cpu",
    deviceLabel: "Browser · WASM (ONNX Runtime Web, single-threaded)",
    error: null,
    remediation: [],
    native: null,
    docker: { available: false, version: null, daemonRunning: false, error: null, images: [], selectedImage: null, gpuRuntimeAvailable: false },
  };
}

export { DWI_STAGES, PWI_STAGES };
export type { DwiStage, PwiStage };
